import { fixedUi } from '@/i18n/fixedUi';
import React, { useState } from 'react';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  rrpMaterialRequirementLabel,
} from '@/shein-platform/modules/discuss/rrpDisplay';
import {
  rrpRuleExplicitlyNoFill,
  siteDisplayLabel,
} from '@/shein-platform/utils/sheinRrpPublish';
import { attributeValueDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 发品向导：按平台 RRP 规则在 SKU 维度填写分站点 / 不分站点建议零售价与证明材料。
 * 规则接口 brand_code 非必传：进页即可查询；有品牌后再带 brand_code 刷新。
 */
export default function SkuRrpFillPanel({
  shopId,
  rule,
  loading = false,
  skcList = [],
  disabled = false,
  readOnlyHint = '',
  brandCode = '',
  onRetryRule,
  onUpdateSku,
}) {
  useTranslation('workbench');
  const [uploadingKey, setUploadingKey] = useState('');

  if (loading) {
    return (
      <div className="spo-rrp-publish" data-field-error="rrp">
        <h4 className="spo-publish__subhead">{chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}</h4>
        <p className="spo-wizard__inline-hint">{chartText('publishExtra.rrpLoading', fixedUi("正在查询建议零售价填写规则…"))}</p>
      </div>
    );
  }

  if (!rule) {
    return (
      <div className="spo-rrp-publish" data-field-error="rrp">
        <h4 className="spo-publish__subhead">{chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}</h4>
        <p className="spo-wizard__inline-hint">
          {chartText('publishExtra.rrpNoRule', '未能获取填写规则，无法判断是否需要填写建议零售价。')}
          {typeof onRetryRule === 'function' ? (
            <>
              {' '}
              <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onRetryRule}>
                {chartText('publishExtra.retryRule', '重新查询')}
              </button>
            </>
          ) : null}
        </p>
      </div>
    );
  }

  if (rrpRuleExplicitlyNoFill(rule)) {
    return (
      <div className="spo-rrp-publish" data-field-error="rrp">
        <h4 className="spo-publish__subhead">{chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}</h4>
        <p className="spo-wizard__inline-hint">
          {chartText('publishExtra.rrpSkip', '当前规则下无需填写建议零售价')}
          {brandCode ? chartText('publishExtra.brandWrap', `（品牌 ${brandCode}）`, { code: brandCode }) : chartText('publishExtra.noBrand', '（未选品牌 / 无品牌）')}
          {chartText('publishExtra.rrpSkipTail', '：分站点与不分站点均为不允许。')}
        </p>
      </div>
    );
  }

  const uploadProof = async (skcId, skuId, rowIndex, field, file) => {
    if (!shopId || !file) return;
    const key = `${skcId}-${skuId}-${rowIndex}-${field}`;
    setUploadingKey(key);
    try {
      const res = await sheinPlatformAPI.uploadDiscussFile(shopId, file, 4);
      const url = res?.url;
      if (!url) throw new Error(chartText('publishExtra.noUrl', '上传未返回 url'));
      const skc = skcList.find((s) => s.id === skcId);
      const sku = skc?.skuRows?.find((r) => r.id === skuId);
      const list = Array.isArray(sku?.siteRrpInfoList) ? [...sku.siteRrpInfoList] : [];
      const row = { ...(list[rowIndex] || {}) };
      row[field] = [...(row[field] || []), url].slice(0, 6);
      list[rowIndex] = row;
      onUpdateSku?.(skcId, skuId, { siteRrpInfoList: list });
      showToast(chartText('publishExtra.proofOk', '证明材料已上传'), 'success');
    } catch (e) {
      showToast(e?.message || chartText('publishExtra.uploadFailed', fixedUi("上传失败")), 'error');
    } finally {
      setUploadingKey('');
    }
  };

  const patchRow = (skcId, skuId, rowIndex, patch) => {
    const skc = skcList.find((s) => s.id === skcId);
    const sku = skc?.skuRows?.find((r) => r.id === skuId);
    const list = Array.isArray(sku?.siteRrpInfoList) ? [...sku.siteRrpInfoList] : [];
    list[rowIndex] = { ...(list[rowIndex] || {}), ...patch };
    onUpdateSku?.(skcId, skuId, { siteRrpInfoList: list });
  };

  return (
    <div className="spo-rrp-publish" data-field-error="rrp">
      <h4 className="spo-publish__subhead">{chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}</h4>
      <div className="spo-batch-modal__hint spo-rrp-rule-box">
        <div>
          填写规则：建议零售价
          {rule.isRrpRequired ? chartText('publishExtra.required', '必填') : chartText('publishExtra.optional', '非必填')}
          {rule.siteFillingRule ? chartText('publishExtra.bySite', ' · 需按站点维度填写') : ''}
          {rule.nonSiteFillingRule ? chartText('publishExtra.otherSiteOk', ' · 其他站点可汇总填写') : ''}
          {brandCode ? chartText('publishExtra.brandDot', ` · 品牌 ${brandCode}`, { code: brandCode }) : chartText('publishExtra.noBrandRule', ' · 未选品牌（规则接口可不传品牌）')}
        </div>
        {readOnlyHint ? <div>{readOnlyHint}</div> : null}
      </div>

      {(skcList || []).filter((skc) => skc.colorValueId || skc.colorExtraValue || skc.colorLabel).map((skc) => (
        (skc.skuRows || []).map((sku) => {
          const list = Array.isArray(sku.siteRrpInfoList) ? sku.siteRrpInfoList : [];
          if (!list.length) return null;
          const colorName = attributeValueDisplayLabel(skc.colorLabel || skc.colorExtraValue) || skc.colorLabel || chartText('publishExtra.colorFallback', fixedUi("颜色"));
          const sizeName = attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || sku.sizeLabel || chartText('publishExtra.specFallback', '规格');
          const siteRows = list.filter((r) => String(r.siteAbbr || '').trim());
          const nonSiteRows = list.filter((r) => !String(r.siteAbbr || '').trim());

          return (
            <div key={`${skc.id}-${sku.id}`} className="spo-rrp-publish__sku">
              <div className="spo-rrp-publish__sku-title">
                {colorName}
                {' · '}
                {sizeName}
              </div>
              <div className="spo-rrp-publish__grid">
                {siteRows.length ? (
                  <div className="spo-rrp-publish__block">
                    <div className="spo-rrp-publish__block-label">{chartText('publishExtra.bySiteRrp', fixedUi("分站点建议零售价"))}</div>
                    <div className="spo-rrp-publish__sites">
                      {siteRows.map((row) => {
                        const idx = list.indexOf(row);
                        return (
                          <div key={`${row.siteAbbr}-${row.currency}`} className="spo-rrp-publish__site">
                            <div className="spo-rrp-publish__site-head">
                              {siteDisplayLabel(row.siteAbbr)}
                              <span className="spo-rrp-publish__cur">{row.currency || '—'}</span>
                            </div>
                            <input
                              className="spo-input"
                              disabled={disabled}
                              value={row.price ?? ''}
                              placeholder={chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}
                              inputMode="decimal"
                              onChange={(e) => patchRow(skc.id, sku.id, idx, { price: e.target.value })}
                            />
                            {Number(row.materialRequirement) > 0 ? (
                              <div className="spo-rrp-publish__proof">
                                <div className="spo-rrp-publish__proof-hint">
                                  {chartText(`publishExtra.rrpMat${Number(row.materialRequirement)}`, rrpMaterialRequirementLabel(row.materialRequirement))}
                                </div>
                                <label className="spo-rrp-publish__upload">
                                  {chartText('publishExtra.uploadType1', '上传类型一材料')}
                                  <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    disabled={disabled || Boolean(uploadingKey)}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = '';
                                      if (f) uploadProof(skc.id, sku.id, idx, 'typeOneAttachments', f);
                                    }}
                                  />
                                </label>
                                <span className="spo-rrp-publish__file-count">
                                  {chartText('publishExtra.fileCount', `${row.typeOneAttachments?.length || 0} 个文件`, { count: row.typeOneAttachments?.length || 0 })}
                                </span>
                                <input
                                  className="spo-input"
                                  disabled={disabled}
                                  value={row.typeOneLink || ''}
                                  placeholder={chartText('publishExtra.type1Link', '类型一证明链接（可选）')}
                                  onChange={(e) => patchRow(skc.id, sku.id, idx, { typeOneLink: e.target.value })}
                                />
                                {Number(row.materialRequirement) === 2 || Number(row.materialRequirement) === 1 ? (
                                  <>
                                    <label className="spo-rrp-publish__upload">
                                      {chartText('publishExtra.uploadType2', '上传类型二材料')}
                                      <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        disabled={disabled || Boolean(uploadingKey)}
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          e.target.value = '';
                                          if (f) uploadProof(skc.id, sku.id, idx, 'typeTwoAttachments', f);
                                        }}
                                      />
                                    </label>
                                    <span className="spo-rrp-publish__file-count">
                                      {chartText('publishExtra.fileCount', `${row.typeTwoAttachments?.length || 0} 个文件`, { count: row.typeTwoAttachments?.length || 0 })}
                                    </span>
                                    <input
                                      className="spo-input"
                                      disabled={disabled}
                                      value={row.typeTwoLink || ''}
                                      placeholder={chartText('publishExtra.type2Link', '类型二证明链接（可选）')}
                                      onChange={(e) => patchRow(skc.id, sku.id, idx, { typeTwoLink: e.target.value })}
                                    />
                                  </>
                                ) : null}
                              </div>
                            ) : (
                              <div className="spo-rrp-publish__proof-hint">{chartText('publishExtra.noProof', fixedUi("本站无需证明材料"))}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {nonSiteRows.length ? (
                  <div className="spo-rrp-publish__block">
                    <div className="spo-rrp-publish__block-label">{chartText('publishExtra.otherSiteRrp', fixedUi("其他站点建议零售价（不分站点汇总）"))}</div>
                    {nonSiteRows.map((row) => {
                      const idx = list.indexOf(row);
                      const currencies = rule.nonSiteCurrencies?.length
                        ? rule.nonSiteCurrencies
                        : [row.currency || 'USD'].filter(Boolean);
                      return (
                        <div key={`non-site-${idx}`} className="spo-rrp-publish__site spo-rrp-publish__site--non">
                          <div className="spo-rrp-publish__site-head">
                            {chartText('publishExtra.otherSites', '其他站点')}
                            <select
                              className="spo-input spo-rrp-publish__cur-select"
                              disabled={disabled}
                              value={row.currency || currencies[0] || ''}
                              onChange={(e) => patchRow(skc.id, sku.id, idx, { currency: e.target.value })}
                            >
                              {currencies.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            className="spo-input"
                            disabled={disabled}
                            value={row.price ?? ''}
                            placeholder={chartText('publishExtra.rrpTitle', fixedUi("建议零售价"))}
                            inputMode="decimal"
                            onChange={(e) => patchRow(skc.id, sku.id, idx, { price: e.target.value })}
                          />
                          <div className="spo-rrp-publish__proof-hint">{chartText('publishExtra.otherNoProof', fixedUi("其他站点无需证明材料"))}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })
      ))}
    </div>
  );
}
